import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  DesignServices as ContentCutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Payment as PaymentIconMui,
} from '@mui/icons-material';
import { usePlacesData } from '../../contexts/PlacesDataContext';
import { useThemeCustom } from '../../hooks/useThemeCustom';
import { getInitials } from '../../utils/professionalNameUtils';
import ProfessionalAvatar from '../ProfessionalAvatar';
import { getIconClass } from '../../utils/iconUtils';
import PaymentIcon from '../ui/PaymentIcon';

/**
 * PlaceCardNormalized
 * 
 * Card que usa estrutura normalizada com lookup de dados via context
 * Não faz mais chamadas API individuais - usa cache local
 */
export default function PlaceCardNormalized({
  place,
  onEdit,
  onDelete,
  onConfigurePayments,
  onManageServices,
  onManageProfessionals,
  onConfigureProfessionalServices,
  onManagePlacePayments,
}) {
  const { getService, getProfessional } = usePlacesData();
  const { getIcon } = useThemeCustom();
  const PlaceIconCmp = getIconClass(getIcon('places').name);

  const profCount = place.professionals?.length || 0;
  const svcCount = place.services?.length || 0;
  const paymentCount = place.payments?.length || 0;

  const renderProfessionalRow = (link) => {
    const professional = getProfessional(link.professionalId);
    if (!professional) return null;

    const initials = getInitials(professional.name || 'Profissional');
    const expandedLink = {
      ...link,
      professional,
      professionalName: professional?.name,
    };
    const profServices = (link.serviceIds || [])
      .map((serviceId) => getService(serviceId))
      .filter(Boolean);
    const subtitle = profServices.length > 0
      ? profServices.map((s) => s.name).join(', ')
      : (professional.specialty || 'Nenhum serviço');

    return (
      <Box
        key={link.id}
        onClick={() => onConfigureProfessionalServices(place, expandedLink)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.875,
          py: 1,
          px: 0.5,
          borderRadius: 0.5,
          minWidth: 0,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ProfessionalAvatar
          professionalId={professional.id}
          photoTimestamp={professional.photoTimestamp}
          name={professional.name}
          sx={{
            bgcolor: 'primary.main',
            width: 34,
            height: 34,
            fontSize: '0.82rem',
            flexShrink: 0,
          }}
        >
          {initials}
        </ProfessionalAvatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ fontSize: '0.86rem', lineHeight: 1.25 }}
          >
            {professional.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word' }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Tooltip title={profServices.length > 0 ? 'Configurar serviços do profissional' : 'Adicionar serviços'}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onConfigureProfessionalServices(place, expandedLink); }}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <SettingsIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderServiceRow = (serviceLink) => {
    const service = getService(serviceLink.serviceId);
    if (!service) return null;

    return (
      <Box
        key={serviceLink.id}
        onClick={() => onConfigurePayments({ ...serviceLink, service, serviceName: service.name })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.75,
          px: 0.5,
          borderRadius: 0.5,
          minWidth: 0,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: '0.55rem', color: 'text.secondary', flexShrink: 0, lineHeight: 1, mt: '1px' }}
        >
          ●
        </Typography>
        <Typography
          variant="body2"
          noWrap
          sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
        >
          {service.name}
        </Typography>
        <Tooltip title="Configurar formas de pagamento">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onConfigurePayments({ ...serviceLink, service, serviceName: service.name }); }}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <PaymentIconMui sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const columnHeader = (icon, label, count, onAdd, addTitle) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 0.5,
        pb: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {icon}
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          letterSpacing: 0.3,
          fontSize: '0.66rem',
          flex: 1,
          color: 'text.primary',
        }}
      >
        {label} ({count})
      </Typography>
      <Tooltip title={addTitle}>
        <IconButton size="small" onClick={onAdd} sx={{ p: 0.25 }}>
          <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1, userSelect: 'none' }}>
            +/−
          </Typography>
        </IconButton>
      </Tooltip>
    </Box>
  );

  const columnBoxSx = {
    minWidth: 0,
    p: 0.75,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: (theme) => theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.035)'
      : 'rgba(99, 115, 129, 0.06)',
  };

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 1.25,
        gap: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.light',
          borderLeftColor: 'primary.main',
        },
      }}
    >
      {/* Header: icon + name + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0 }}>
        <PlaceIconCmp sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
        <Tooltip title={place.description || ''} disableHoverListener={!place.description}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ fontSize: '1.05rem', flex: 1, minWidth: 0, lineHeight: 1.25 }}
          >
            {place.name}
          </Typography>
        </Tooltip>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          <Tooltip title="Editar local">
            <IconButton size="small" onClick={() => onEdit(place)} color="primary" sx={{ p: 0.5 }}>
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir local">
            <IconButton size="small" onClick={() => onDelete(place)} color="error" sx={{ p: 0.5 }}>
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Meta row: address + phone compact */}
      {(place.address || place.phone) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0,
            flexShrink: 0,
            color: 'text.secondary',
          }}
        >
          {place.address && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, flex: 1 }}>
              <LocationIcon sx={{ fontSize: 13, flexShrink: 0 }} />
              <Typography variant="caption" noWrap sx={{ fontSize: '0.72rem' }}>
                {place.address}
              </Typography>
            </Box>
          )}
          {place.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              <PhoneIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                {place.phone}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Pagamentos: faixa compacta no topo (abaixo dos botões de ação), só ícones */}
      {paymentCount > 0 && (
        <Box
          onClick={() => onManagePlacePayments?.(place)}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            justifyContent: 'flex-end',
            alignItems: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {place.payments.map((payment) => (
            <Tooltip key={payment.id} title={payment.name || 'Forma de pagamento'} enterDelay={200}>
              <Box sx={{ display: 'inline-flex', lineHeight: 0 }}>
                <PaymentIcon displayOrder={payment.displayOrder ?? 1} size={26} />
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Two-column content: Serviços | Profissionais */}
      <Box
        sx={{
          mt: 0.25,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          columnGap: 1,
          rowGap: 1,
          maxHeight: 280,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.5,
          mr: -0.5,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'action.disabledBackground',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'action.disabled' },
        }}
      >
        {/* Serviços (esquerda) */}
        <Box sx={columnBoxSx}>
          {columnHeader(
            <ContentCutIcon sx={{ fontSize: 14, color: 'primary.main' }} />,
            'Serviços',
            svcCount,
            () => onManageServices(place),
            'Gerenciar serviços'
          )}
          {svcCount > 0 ? (
            place.services.map(renderServiceRow)
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', fontSize: '0.7rem', display: 'block', px: 0.5 }}
            >
              Nenhum serviço vinculado
            </Typography>
          )}
        </Box>

        {/* Profissionais (direita) */}
        <Box sx={columnBoxSx}>
          {columnHeader(
            <PersonIcon sx={{ fontSize: 14, color: 'primary.main' }} />,
            'Profissionais',
            profCount,
            () => onManageProfessionals(place),
            'Gerenciar profissionais'
          )}
          {profCount > 0 ? (
            place.professionals.map(renderProfessionalRow)
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', fontSize: '0.7rem', display: 'block', px: 0.5 }}
            >
              Nenhum profissional vinculado
            </Typography>
          )}
        </Box>

      </Box>

      {/* Footer: aparece apenas quando nao ha formas de pagamento vinculadas */}
      {paymentCount === 0 && (
        <Box
          sx={{
            mt: 'auto',
            pt: 0.75,
            borderTop: '1px dashed',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: 'italic', fontSize: '0.72rem' }}
          >
            Nenhuma forma de pagamento vinculada
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<PaymentIconMui sx={{ fontSize: 14 }} />}
            onClick={() => onManagePlacePayments?.(place)}
            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}
          >
            Adicionar
          </Button>
        </Box>
      )}
    </Card>
  );
}

PlaceCardNormalized.propTypes = {
  place: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onConfigurePayments: PropTypes.func,
  onManageServices: PropTypes.func,
  onManageProfessionals: PropTypes.func,
  onConfigureProfessionalServices: PropTypes.func,
  onManagePlacePayments: PropTypes.func,
};
